import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ChartData, ChartOptions, TooltipItem, TooltipModel } from 'chart.js';
import {} from 'ng2-charts';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { LocationStoreSelectors, RootStoreState } from 'src/app/root-store';
import { ServiceProvider } from 'src/app/service';
import { SubSink } from 'subsink';
import { SurveyReport } from '../../models/SurveyReport.model';

@Component({
    selector: 'app-survey-report',
    templateUrl: './survey-report.component.html',
    styleUrls: ['./survey-report.component.css'],
    standalone: false
})
export class SurveyReportComponent implements OnInit, OnDestroy {
  public _subs = new SubSink();
  public report: SurveyReport;
  public startDate: Date;
  public endDate: Date;
  public locationId: number;
  //charts
  public chartLabels: string[] = ['Pre-Appointment', 'Arrival', 'Appointment', 'Referral', 'Points Missed'];
  public chartLabelsIndividual: string[] = ['Score', 'Missed'];
  public chartOptions: ChartOptions<'doughnut'> = {
    cutout: 80,
    rotation: -180,
    aspectRatio: 1,
    plugins: {
      tooltip: {
        callbacks: {
          beforeLabel: this._labelCallBack,
        },
      },
    },
  };
  public chartColors = ['#13a9de', '#bdcfd1', '#e32120', '#0e2336', '#FFFFFF'];
  public chartDataSets: SurveyReortChartDataset;

  constructor(private _remoteService: ServiceProvider, private _store$: Store<RootStoreState.State>) {}

  ngOnInit(): void {
    this._subs.sink = this._store$
      .select(LocationStoreSelectors.getSelectedLocationId)
      .pipe(
        distinctUntilChanged(),
        filter((x) => !!x)
      )
      .subscribe((locationId) => (this.locationId = locationId));
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  public loadReport(LocationId: number, StartDate: Date = null, EndDate: Date = null): void {
    let params = new HttpParams();
    if (StartDate) params = params.append('StartDate', StartDate.toJSON());
    if (EndDate) params = params.append('EndDate', EndDate.toJSON());

    this._subs.sink = this._remoteService
      .get<SurveyReport>(`NewPatientTracker/GetSurveyReport/${LocationId}`, params)
      .pipe(
        map((response) => {
          response.startDate = new Date(response.startDate);
          response.endDate = new Date(response.endDate);
          return response;
        })
      )
      .subscribe((response) => {
        this.report = response;
        this.startDate = this.report.startDate;
        this.endDate = this.report.endDate;
        this._buildChart();
        // Uncommment following lines to view report when no data present
        // this.chartDataSets = this.mockChartDataSets;
        // this.report.count = 5;
      });
  }

  public getOutOfTheSurveyInfoInPercents(value: number, count: number): number {
    return (value / count) * 100;
  }

  public getOutOfTheSurveyInfoInPercentsValue(value: number, count: number): number {
    return parseFloat(Math.round((value / count) * 100).toFixed(2));
  }

  private _buildChart(): void {
    this.chartDataSets = {
      total: null,
      totalLabel: { grade: null, percentage: null },
      preAppointment: null,
      preAppointmentLabel: { grade: null, percentage: null },
      arrival: null,
      arrivalLabel: { grade: null, percentage: null },
      appointment: null,
      appointmentLabel: { grade: null, percentage: null },
      referral: null,
      referralLabel: { grade: null, percentage: null },
    };

    // TOTOAL DATASET
    const totalValue =
      this.report.scores.preAppointment + this.report.scores.arrival + this.report.scores.appointment + this.report.scores.referral * 2;
    const totalPerc = totalValue / (this.report.count * 105);
    this.chartDataSets.total = {
      datasets: [
        {
          data: [
            this.report.scores.preAppointment,
            this.report.scores.arrival,
            this.report.scores.appointment,
            this.report.scores.referral * 2,
            this.report.count * 105 - totalValue,
          ],
          backgroundColor: this.chartColors,
        },
      ],
    };
    this.chartDataSets.totalLabel.grade = this._getGrade(totalPerc);
    this.chartDataSets.totalLabel.percentage = (totalPerc * 100).toFixed(1) + '%';

    // PREAPPT DATASET
    const preAppointmentPerc = this.report.scores.preAppointment / (this.report.count * 20);
    this.chartDataSets.preAppointment = {
      datasets: [
        {
          data: [this.report.scores.preAppointment, this.report.count * 20 - this.report.scores.preAppointment],
          backgroundColor: [this.chartColors[0], '#FFF'],
        },
      ],
    };
    this.chartDataSets.preAppointmentLabel.grade = this._getGrade(preAppointmentPerc);
    this.chartDataSets.preAppointmentLabel.percentage = (preAppointmentPerc * 100).toFixed(1) + '%';

    // ARRIVAL DATASET
    const arrivalPerc = this.report.scores.arrival / (this.report.count * 20);
    this.chartDataSets.arrival = {
      datasets: [
        {
          data: [this.report.scores.arrival, this.report.count * 20 - this.report.scores.arrival],
          backgroundColor: [this.chartColors[1], '#FFF'],
        },
      ],
    };
    this.chartDataSets.arrivalLabel.grade = this._getGrade(arrivalPerc);
    this.chartDataSets.arrivalLabel.percentage = (arrivalPerc * 100).toFixed(1) + '%';

    // APPT DATASET
    const appointmentPerc = this.report.scores.appointment / (this.report.count * 35);
    this.chartDataSets.appointment = {
      datasets: [
        {
          data: [this.report.scores.appointment, this.report.count * 35 - this.report.scores.appointment],
          backgroundColor: [this.chartColors[2], '#FFF'],
        },
      ],
    };
    this.chartDataSets.appointmentLabel.grade = this._getGrade(appointmentPerc);
    this.chartDataSets.appointmentLabel.percentage = (appointmentPerc * 100).toFixed(1) + '%';

    // REFERRAL DATASET
    const referralPerc = this.report.scores.referral / (this.report.count * 20);
    this.chartDataSets.referral = {
      datasets: [
        {
          data: [this.report.scores.referral, this.report.count * 15 - this.report.scores.referral],
          backgroundColor: [this.chartColors[3], '#FFF'],
        },
      ],
    };
    this.chartDataSets.referralLabel.grade = this._getGrade(referralPerc);
    this.chartDataSets.referralLabel.percentage = (referralPerc * 100).toFixed(1) + '%';
  }

  private _labelCallBack(this: TooltipModel<'doughnut'>, tooltipItem: TooltipItem<'doughnut'>): string {
    const dataset = tooltipItem.dataset;
    const total = (dataset.data as number[]).reduce((a, b) => a + b);
    const currentValue = dataset.data[tooltipItem.dataIndex] as number;
    const percentage = parseFloat(((currentValue / total) * 100).toFixed(1));
    return `${tooltipItem.label} (${percentage}%)`;
  }

  private _getGrade(score: number): string {
    score = score * 100;
    if (score >= 96) return 'A+';
    else if (score >= 93) return 'A';
    else if (score >= 90) return 'A-';
    else if (score >= 86) return 'B+';
    else if (score >= 83) return 'B';
    else if (score >= 80) return 'B-';
    else if (score >= 76) return 'C+';
    else if (score >= 73) return 'C';
    else if (score >= 70) return 'C-';
    else if (score >= 66) return 'D+';
    else if (score >= 63) return 'D';
    else if (score >= 60) return 'D-';
    else if (score >= 56) return 'F+';
    else if (score >= 53) return 'F-';

    return 'F-';
  }

  mockChartDataSets: SurveyReortChartDataset = {
    total: {
      datasets: [
        {
          data: [10, 20, 30, 40, 50],
          backgroundColor: this.chartColors,
        },
      ],
    },
    totalLabel: { grade: 'A', percentage: '10%' },
    preAppointment: {
      datasets: [
        {
          data: [10, 20],
          backgroundColor: [this.chartColors[0], '#FFF'],
        },
      ],
    },
    preAppointmentLabel: { grade: 'B', percentage: '20%' },
    arrival: {
      datasets: [
        {
          data: [30, 40],
          backgroundColor: [this.chartColors[1], '#FFF'],
        },
      ],
    },
    arrivalLabel: { grade: 'C', percentage: '30%' },
    appointment: {
      datasets: [
        {
          data: [50, 60],
          backgroundColor: [this.chartColors[2], '#FFF'],
        },
      ],
    },
    appointmentLabel: { grade: 'D', percentage: '40%' },
    referral: {
      datasets: [
        {
          data: [70, 80],
          backgroundColor: [this.chartColors[3], '#FFF'],
        },
      ],
    },
    referralLabel: { grade: 'E', percentage: '50%' },
  };
}

interface SurveyReortChartDataset {
  total: ChartData;
  totalLabel: { grade: string; percentage: string };
  preAppointment: ChartData;
  preAppointmentLabel: { grade: string; percentage: string };
  arrival: ChartData;
  arrivalLabel: { grade: string; percentage: string };
  appointment: ChartData;
  appointmentLabel: { grade: string; percentage: string };
  referral: ChartData;
  referralLabel: { grade: string; percentage: string };
}
